import React from 'react';
import { ArrowLeft } from 'lucide-react';

const TermsOfServiceView = ({ setCurrentView }) => {
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6 md:p-8">
        <button
          onClick={() => setCurrentView('login')}
          className="flex items-center text-blue-600 hover:text-blue-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-gray-600 mb-6">Last Updated: November 17, 2025</p>

        <div className="space-y-6 text-gray-700">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
            <p>
              Welcome to RepConnect ("Service", "Platform", "we", "us", or "our"). By accessing or using
              RepConnect at meetwithreps.com, you agree to be bound by these Terms of Service
              ("Terms"). If you do not agree to these Terms, please do not use our Service.
            </p>
            <p className="mt-2">
              These Terms constitute a legally binding agreement between you and RepConnect, LLC,
              the operator of RepConnect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">2. Description of Service</h2>
            <p>
              RepConnect is a healthcare meeting scheduling platform that facilitates connections
              between pharmaceutical sales representatives and healthcare providers for professional
              lunch meetings and educational discussions.
            </p>
            <p className="mt-2">
              The Service provides:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Meeting scheduling and calendar management</li>
              <li>Provider search and discovery tools</li>
              <li>Restaurant and meal ordering coordination</li>
              <li>Compliance tracking and reporting</li>
              <li>Communication tools between sales representatives and healthcare providers</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">3. User Accounts and Eligibility</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.1 Account Registration</h3>
            <p>
              To use RepConnect, you must create an account and provide accurate, complete, and current
              information. You are responsible for maintaining the confidentiality of your account
              credentials and for all activities that occur under your account.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.2 Eligibility Requirements</h3>
            <p>You represent and warrant that:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>You are at least 18 years of age</li>
              <li>You have the legal capacity to enter into binding contracts</li>
              <li>For Sales Representatives: You are employed by or representing a legitimate pharmaceutical or medical device company</li>
              <li>For Healthcare Providers: You are a licensed healthcare professional (physician, NP, PA, etc.)</li>
              <li>Your use of the Service complies with all applicable laws and regulations</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.3 Account Security</h3>
            <p>
              You must immediately notify us of any unauthorized use of your account or any other
              security breach. We are not liable for any loss or damage arising from your failure
              to protect your account credentials.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">4. Subscription Plans and Payments</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.1 Subscription Tiers</h3>
            <p>RepConnect offers the following subscription plans for sales representatives:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Professional Plan:</strong> $49/month - Individual sales representative access</li>
              <li><strong>Team Plan:</strong> $99/month - Team management and multi-user access</li>
            </ul>
            <p className="mt-2">
              Healthcare providers may access basic features at no cost, with optional premium features
              available for a fee.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.2 Payment Terms</h3>
            <p>
              Subscriptions are billed on a monthly recurring basis. By subscribing, you authorize us
              to charge your payment method on file for the applicable subscription fee plus any
              applicable taxes. All payments are processed securely through Stripe.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.3 Automatic Renewal</h3>
            <p>
              Your subscription will automatically renew each month unless you cancel before the
              renewal date. You may cancel your subscription at any time through your account settings.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.4 Refund Policy</h3>
            <p>
              Subscription fees are non-refundable except as required by law. If you cancel your
              subscription, you will retain access to paid features until the end of your current
              billing period.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.5 Price Changes</h3>
            <p>
              We reserve the right to modify our pricing at any time. We will provide at least 30 days'
              notice of any price changes. Your continued use of the Service after the price change
              constitutes acceptance of the new price.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">5. User Conduct and Responsibilities</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">5.1 Acceptable Use</h3>
            <p>You agree to use RepConnect only for lawful purposes and in accordance with these Terms. You agree NOT to:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Violate any applicable laws, regulations, or third-party rights</li>
              <li>Use the Service for any fraudulent or unauthorized purpose</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Harass, abuse, or harm another person</li>
              <li>Share false, misleading, or inaccurate information</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to any portion of the Service</li>
              <li>Use automated systems (bots, scrapers) without our written permission</li>
              <li>Collect or store personal data of other users without their consent</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">5.2 Healthcare Compliance</h3>
            <p>
              Sales representatives and healthcare providers must comply with all applicable healthcare
              regulations, including but not limited to:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Anti-Kickback Statute (AKS)</li>
              <li>PhRMA Code on Interactions with Healthcare Professionals</li>
              <li>Sunshine Act reporting requirements</li>
              <li>State-specific gift and meal regulations</li>
              <li>HIPAA privacy and security rules</li>
            </ul>
            <p className="mt-2">
              RepConnect provides tools to facilitate compliance but does not guarantee compliance.
              You are solely responsible for ensuring your use of the Service complies with all
              applicable laws and regulations.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">5.3 Meeting Conduct</h3>
            <p>
              All meetings scheduled through RepConnect must be professional and comply with applicable
              regulations. Sales representatives are responsible for:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Ensuring meal values comply with applicable spending limits</li>
              <li>Maintaining accurate records for compliance reporting</li>
              <li>Respecting provider preferences and boundaries</li>
              <li>Canceling meetings in a timely manner if necessary</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">6. Intellectual Property Rights</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">6.1 Ownership</h3>
            <p>
              RepConnect and all content, features, and functionality (including but not limited to
              software, text, graphics, logos, and images) are owned by RepConnect, LLC, and are
              protected by copyright, trademark, and other intellectual property laws.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">6.2 Limited License</h3>
            <p>
              Subject to these Terms, we grant you a limited, non-exclusive, non-transferable,
              revocable license to access and use RepConnect for your personal or internal business
              purposes.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">6.3 User Content</h3>
            <p>
              You retain ownership of any content you submit to RepConnect (profile information,
              meeting notes, etc.). By submitting content, you grant us a worldwide, non-exclusive,
              royalty-free license to use, store, and display such content as necessary to provide
              the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">7. Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Our collection, use, and disclosure of your personal
              information is governed by our Privacy Policy, which is incorporated into these Terms
              by reference. By using RepConnect, you consent to our collection and use of your
              information as described in the Privacy Policy.
            </p>
            <p className="mt-2">
              We implement industry-standard security measures to protect your data, but cannot
              guarantee absolute security. You use the Service at your own risk.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">8. Third-Party Services</h2>
            <p>
              RepConnect integrates with third-party services including:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Stripe for payment processing</li>
              <li>Supabase for data storage</li>
              <li>Restaurant and food delivery services</li>
              <li>Email and communication services</li>
            </ul>
            <p className="mt-2">
              Your use of these third-party services is subject to their respective terms and privacy
              policies. We are not responsible for the actions or policies of third-party service providers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">9. Disclaimers and Limitations of Liability</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">9.1 Service "As Is"</h3>
            <p>
              REPCONNECT IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">9.2 No Guarantee of Results</h3>
            <p>
              We do not guarantee that RepConnect will meet your requirements, be uninterrupted,
              timely, secure, or error-free. We do not guarantee specific results from using the Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">9.3 Limitation of Liability</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, REPCONNECT, LLC, AND ITS AFFILIATES, OFFICERS,
              DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED
              DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES
              RESULTING FROM:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Your use or inability to use the Service</li>
              <li>Any unauthorized access to or use of our servers and/or any personal information stored therein</li>
              <li>Any interruption or cessation of transmission to or from the Service</li>
              <li>Any bugs, viruses, or malware transmitted through the Service</li>
              <li>Any errors or omissions in any content or for any loss or damage incurred as a result of your use of any content</li>
            </ul>
            <p className="mt-2">
              IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU EXCEED THE AMOUNT YOU PAID US IN THE TWELVE
              (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE LIABILITY, OR ONE HUNDRED DOLLARS
              ($100), WHICHEVER IS GREATER.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless RepConnect, LLC, and its affiliates,
              officers, directors, employees, and agents from and against any claims, liabilities,
              damages, losses, costs, expenses, or fees (including reasonable attorneys' fees)
              arising from:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another person or entity</li>
              <li>Your violation of any applicable laws or regulations</li>
              <li>Any content you submit to the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">11. Termination</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">11.1 Termination by You</h3>
            <p>
              You may terminate your account at any time by contacting us or using the account
              cancellation feature in your settings. Upon termination, your subscription will not
              renew, but you will retain access until the end of your current billing period.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">11.2 Termination by Us</h3>
            <p>
              We may terminate or suspend your account and access to the Service immediately, without
              prior notice or liability, for any reason, including but not limited to:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Violation of these Terms</li>
              <li>Fraudulent, abusive, or illegal activity</li>
              <li>Non-payment of fees</li>
              <li>At our sole discretion</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">11.3 Effect of Termination</h3>
            <p>
              Upon termination, your right to use the Service will immediately cease. We may delete
              your account and all associated data. Provisions of these Terms that by their nature
              should survive termination shall survive, including ownership provisions, warranty
              disclaimers, indemnity, and limitations of liability.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">12. Dispute Resolution</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">12.1 Informal Resolution</h3>
            <p>
              Before filing a claim, you agree to contact us at support@meetwithreps.com to attempt
              to resolve the dispute informally. We will attempt to resolve the dispute within 30 days.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">12.2 Governing Law</h3>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the
              State of California, without regard to its conflict of law provisions.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">12.3 Arbitration</h3>
            <p>
              Any dispute arising from these Terms or your use of the Service shall be resolved
              through binding arbitration in accordance with the American Arbitration Association's
              rules, rather than in court, except that you may assert claims in small claims court
              if your claims qualify.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">13. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material
              changes by posting the new Terms on the Service and updating the "Last Updated" date.
              Your continued use of the Service after such changes constitutes your acceptance of
              the new Terms.
            </p>
            <p className="mt-2">
              It is your responsibility to review these Terms periodically for changes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">14. General Provisions</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">14.1 Entire Agreement</h3>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between
              you and RepConnect, LLC regarding the Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">14.2 Severability</h3>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision
              shall be limited or eliminated to the minimum extent necessary so that these Terms shall
              otherwise remain in full force and effect.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">14.3 Waiver</h3>
            <p>
              Our failure to enforce any right or provision of these Terms will not be deemed a waiver
              of such right or provision.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">14.4 Assignment</h3>
            <p>
              You may not assign or transfer these Terms or your rights hereunder without our prior
              written consent. We may assign these Terms without restriction.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">14.5 Force Majeure</h3>
            <p>
              We shall not be liable for any failure to perform our obligations under these Terms
              where such failure results from causes beyond our reasonable control.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">15. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold">RepConnect, LLC</p>
              <p className="mt-1">Email: support@meetwithreps.com</p>
              <p>Website: meetwithreps.com</p>
            </div>
          </section>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              By using RepConnect, you acknowledge that you have read, understood, and agree to be
              bound by these Terms of Service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServiceView;
